import {
  apiClient,
  deletePersona,
  getDocuments,
  PersonaDocuments,
} from "@/api/api.ts";
import { useQueries, useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useMutation, useQueryClient } from "@tanstack/react-query";

export type PersonaId = string;

export type TokenStage = "minted" | "graduated";
export type MarketplaceNearGraduation = "near-graduation";

export type Persona = {
  persona_id: PersonaId;
  name: string;
  twitter: string;
  avatar_id: string;
  age?: string | number | null;
  gender?: "male" | "female" | "";
  occupation?: string;
  appearance?: {
    hair_color?: string;
    eye_color?: string;
    skin_tone?: string;
    body_type?: string;
    fashion?: string;
    height?: string;
    weight?: string;
    ethnicity?: string;
  };
  email?: string;
  instagram_data?: {
    permissions: string[];
    user_id: string;
    username: string;
  };
  linktree?: string;
  organization?: string;
  organization_logo_url?: string;
  persona_video_avatar?: string;
  short_bio?: string;
  bio?: string;
  persona_worldview?: string;
  content_strategy?: string;
  interests?: string[];
  usernames_to_engage?: string[];
  twitter_lists?: string[];
  telegram?: string;
  persona?: string;
  style?: string;
  target_audience?: string[];
  persona_objective?: string;
  public_persona?: boolean;
  enable_image_generation?: boolean;
  enable_twitter_engagement?: boolean;
  persona_owner?: string;
  qstarlabs_tenant?: string;
  coin_data?: Array<{
    days: string;
    price: number;
  }>;
  web3?: {
    tokenStage?: TokenStage;
    metadata?: {
      name: string;
      symbol: string;
      description: string;
      image: string;
    };
    metadataUri?: string;
    blockchain?: string;
    contractAddress?: string;
    cluster?: string;
    pool?: string;
    bondingCurve: {
      poolAddress: string;
      poolTokenAccount: string;
      poolSolVault: string;
    };
    raydium: {
      poolAddress: string;
    };
    signatures: {
      paymentSignature: string;
      mintSignature: string;
    };
  };
  custom_prompt_rules?: string[];
  created_at?: string;
  /* Non-exhaustive type. There's some missing properties here. */
};

const fetchPersonaData = async (authenticated = true) => {
  let personasArray: Persona[] = [];
  try {
    const url = authenticated ? `/api/personas` : `/api/personas/public`;
    const response = await apiClient.get(url);
    // Convert the dictionary of personas to an array
    personasArray = Object.keys(response.data).map((personaId) => ({
      id: personaId,
      persona_id: personaId,
      ...response.data[personaId],
    }));
  } catch (error) {
    console.error("Error fetching agents:", error);
    throw error;
  }

  if (personasArray.length === 0) {
    console.warn("Agents data is empty after conversion.");
  }

  return personasArray;
};

export type JobState = "queued" | "running" | "success" | "failed"; // https://github.com/apache/airflow-client-python/blob/main/docs/DagState.md
export interface JobStatus {
  state: JobState;
  url: string;
  execution_date?: string;
}

async function fetchPersonaJobStatus(personaId: string): Promise<JobStatus[]> {
  try {
    const url = `/api/personas/${personaId}/status`;
    const response = await apiClient.get(url);

    const jobsStatus: JobStatus[] = response.data;

    return jobsStatus.sort(
      (a, b) =>
        Date.parse(a.execution_date || "") - Date.parse(b.execution_date || ""),
    );
  } catch (error) {
    console.error("Error fetching agents:", error);
    throw error;
  }
}

export function usePersonasQuery<T = Persona[]>(
  authenticated = true,
  options?: Pick<UseQueryOptions<Persona[], Error, T>, "select">,
) {
  return useQuery({
    ...options,
    ...agentQueries.agents(authenticated),
  });
}

export function usePersonaQuery(authenticated = true, personaId: string) {
  return useQuery(agentQueries.agent(personaId, authenticated));
}

export function usePersonaWithDocumentsQuery(
  personaId: string,
  authenticated = true,
) {
  return useQueries({
    queries: [
      {
        queryKey: ["personas", authenticated],
        queryFn: () => fetchPersonaData(authenticated),
        select: (personas: Persona[]) =>
          personas.find((p) => p.persona_id === personaId),
      },
      {
        queryKey: ["personas", personaId, "documents", authenticated],
        queryFn: () => getDocuments(personaId, authenticated),
      },
    ],
    combine: ([personaQueryResult, documentsQueryResult]) => ({
      data:
        personaQueryResult.data && documentsQueryResult.data
          ? {
              persona: personaQueryResult.data,
              documents: documentsQueryResult.data,
            }
          : undefined,
      isError: personaQueryResult.isError || documentsQueryResult.isError,
      isLoading: personaQueryResult.isLoading || documentsQueryResult.isLoading,
    }),
  });
}

export function useDeletePersonaDetailsMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (personaId: string) => deletePersona(personaId),
    onSuccess: (_, personaId) => {
      queryClient.setQueriesData(
        { queryKey: ["personas"] },
        (prev: Persona[] | PersonaDocuments | undefined) => {
          if (!prev) return;
          if (Array.isArray(prev)) {
            return prev.filter((persona) => persona.persona_id !== personaId);
          } else {
            return null;
          }
        },
      );
    },
  });
}

const fetchPersonaDataNoPublic = async (authenticated = true) => {
  let personasArray: Persona[] = [];
  try {
    // If you prefer a different route, just adjust the URL accordingly
    const url = authenticated
      ? `/api/personas/no_public`
      : `/api/personas/public`;
    const response = await apiClient.get(url);
    // Convert the dictionary of personas to an array
    personasArray = Object.keys(response.data).map((personaId) => ({
      id: personaId,
      persona_id: personaId,
      ...response.data[personaId],
    }));
  } catch (error) {
    console.error("Error fetching 'no public' agents:", error);
    throw error;
  }

  if (personasArray.length === 0) {
    console.warn("'No Public' agents data is empty after conversion.");
  }
  return personasArray;
};

export function usePersonasQueryNoPublic<T = Persona[]>(
  authenticated = true,
  options?: Pick<UseQueryOptions<Persona[], Error, T>, "select">,
) {
  return useQuery({
    ...options,
    queryKey: ["personas", authenticated, "no_public"],
    queryFn: () => fetchPersonaDataNoPublic(authenticated),
    // Don't attempt to fetch if not authenticated
    enabled: authenticated,
  });
}

export async function updatePersonaValues(
  personaId: string,
  updates: Record<string, any>,
): Promise<{ message: string; updated_fields: string[] }> {
  try {
    // Example path: PATCH /api/personas/{persona_id}/values
    const response = await apiClient.patch(
      `/api/personas/${personaId}/values`,
      updates,
    );
    return response.data; // { message, updated_fields }
  } catch (error) {
    console.error("Error updating persona values:", error);
    throw error;
  }
}

export async function fetchSuggestedKOLs(personaId: string): Promise<string[]> {
  try {
    const response = await apiClient.get(
      `/api/personas/${personaId}/suggested_kols`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching persona suggested KOLs:", error);
    throw error;
  }
}

export const agentQueries = {
  agents: (authenticated = true) => ({
    queryKey: ["personas", authenticated],
    queryFn: () => fetchPersonaData(authenticated),
  }),
  agent: (id: string, authenticated = true) => ({
    ...agentQueries.agents(authenticated),
    select: (personas: Persona[]) => personas.find((p) => p.persona_id === id),
  }),
  agentJobStatus: (personaId: string) => ({
    queryKey: ["persona", personaId, "job-status"],
    queryFn: () => fetchPersonaJobStatus(personaId),
  }),
};

export interface QuickCreatePersonaInput {
  name: string;
  objective?: string;
}

export interface QuickCreatePersonaResponse {
  message: string;
  persona: Persona; // Assuming the backend returns the created persona object
}

export async function quickCreatePersona(
  input: QuickCreatePersonaInput,
): Promise<QuickCreatePersonaResponse> {
  try {
    const response = await apiClient.post<QuickCreatePersonaResponse>(
      `/api/personas/quick-create`,
      input,
    );
    return response.data;
  } catch (error) {
    console.error("Error during quick persona creation:", error);
    // Rethrow or handle error as appropriate for your UI
    throw error;
  }
}

// Renamed Interface for clarity
export interface QuickPopulatePersonaInput {
  persona_id: string;
}

// Response includes the potentially updated persona
export interface QuickPopulatePersonaResponse {
  message: string;
  persona: Persona;
}

// Renamed function for clarity
export async function quickPopulatePersona(
  input: QuickPopulatePersonaInput,
): Promise<QuickPopulatePersonaResponse> {
  try {
    // Call the updated endpoint
    const response = await apiClient.post<QuickPopulatePersonaResponse>(
      `/api/personas/quick-create`, // Endpoint path remains the same for now
      input, // Send { persona_id: "..." }
    );
    return response.data;
  } catch (error) {
    console.error("Error during quick persona population:", error);
    throw error; // Rethrow or handle error as appropriate for your UI
  }
}

// --- AI Generate Details --- //
export interface AIGenerateDetailsInput {
  persona_id: string;
  fields_to_generate: string[];
  feedback?: string; // Added optional feedback
  preview_only?: boolean; // Add preview mode to avoid saving changes
}

// Response now contains the full updated persona
export interface AIGenerateDetailsResponse {
  message: string;
  persona: Persona; // Expecting the updated persona object
}

export async function aiGenerateDetails(
  input: AIGenerateDetailsInput,
): Promise<AIGenerateDetailsResponse> {
  try {
    const response = await apiClient.post<AIGenerateDetailsResponse>(
      `/api/personas/ai-generate-details`, // Use the new endpoint path
      input,
    );
    return response.data;
  } catch (error) {
    console.error("Error during AI detail generation:", error);
    throw error;
  }
}
// ------------------------ //
